import { createSelector } from "@ngrx/store";
import { isBefore, parseISO } from "date-fns";
import { orderBy } from "lodash-es";
import { State } from "../reducers";
import { getUserDateRange } from "../user-settings";
import { Lot } from "./lot";
import { LotsTableFilter } from "./lot.reducers";
const lotsStateSelector = (state: State) => state.lots;

export const getLots = createSelector(
  lotsStateSelector,
  (state) => state.lots
);

export const getAmounts = createSelector(
  lotsStateSelector,
  (state) => state.lotAmounts
);

export const getLot = (lotId: string) => createSelector(
  getLots,
  getAmounts,
  (lots, amounts) => {
    if (!lotId) {
      return null;
    }
    const lot = lots.find(l => l.id === lotId);
    if (!lot) {
      return null;
    }
    return {
      ...lot,
      amounts: amounts.filter(amt => amt.lot?.id === lotId)
    };
  }
);

export const getTableFilter = createSelector(
  lotsStateSelector,
  getUserDateRange,
  (state, dateRange) => ({
    query: state.tableFilter.query,
    dateRange,
    publicStatus: state.tableFilter.publicStatus ?? null
  })
);

export const getFilteredLots = createSelector(
  getLots,
  getTableFilter,
  (lots, tableFilter) => sortLotsByDateDesc(filterLots(lots, tableFilter))
);

export const getBeekeeperProductsList = createSelector(
  lotsStateSelector,
  (state) => state.beekeeperProducts
);

/**
 * Get the (one) BeekeeperProducts entry for the current beekeeper.
 * Returns the first item from the list or null if none exists.
 */
export const getBeekeeperProducts = createSelector(
  getBeekeeperProductsList,
  (products) => products.length > 0 ? products[0] : null
);

/**
 * Take the list of lots, apply all filters and return the filterd list of lots.
 * @param lots the full lits of lots
 * @param filter the table filter
 * @returns the filtered lots
 */
function filterLots(lots: Lot[], filter: LotsTableFilter) {
  lots = lots || [];
  // Filter by date
  lots = lots.filter(lot => {
    // If no full date range (start end end), return all entries
    // TODO: make this work with one-sided ranges ?
    if (!filter.dateRange || !filter.dateRange.start || !filter.dateRange.end) {
      return true;
    }
    // NOTE: mostly, the lot.created_date will not be a date but string - FIXME
    const lotCreateDate = parseISO('' + lot.create_date);
    const lotUpdateDate = parseISO('' + lot.update_date);
    return (isBefore(lotCreateDate, filter.dateRange.end) && isBefore(filter.dateRange.start, lotCreateDate)) ||
      (isBefore(lotUpdateDate, filter.dateRange.end) && isBefore(filter.dateRange.start, lotUpdateDate));
  });

  if (filter.publicStatus !== null) {
    lots = lots.filter(lot => !!lot.public === filter.publicStatus);
  }

  // Filter by text
  if (filter.query) {
    const query = filter.query.toLowerCase();
    lots = lots.filter(lot => {
      const searchableText = [
        lot.lotCode,
        lot.number,
        lot.comment,
        lot.productTitle,
      ]
        .filter(Boolean) // Remove null/undefined
        .join('----')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }
  return lots;
}

function sortLotsByDateDesc(lots: Lot[]) {
  return orderBy(
    lots,
    [
      (lot) => lot.harvestToDate || lot.harvestFromDate || lot.update_date || lot.create_date || null,
      (lot) => lot.harvestFromDate || lot.update_date || lot.create_date || null,
    ],
    ['desc', 'desc']
  );
}
